import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";
import useStorePlayer from "store/useStorePlayer";
import useStoreUserPanel from "store/useStoreUserPanel";
import Avatar from "component/Ui/Avatar";
import LevelBadge from "component/Ui/LevelBadge";
import getPublicAssetUrl from "utils/PublicAsset";

const ProfileTab = () => {
    const { t } = useTranslation();
    const setActiveAccountTab = useStoreUserPanel((state) => state.setActiveAccountTab);
    const { statistics, avatar, flag, username, level } = useStorePlayer(useShallow((state) => ({
        statistics: state.player?.statistics ?? null,
        avatar: state.player?.avatar ?? "",
        flag: state.player?.flag ?? "",
        username: state.player?.username ?? "",
        level: state.player?.level ?? 1,
    })));

    if(!username) {
        return null;
    }

    const stats = statistics;

    return (
        <div className="flex flex-col gap-3 mt-4">
            {/* CNI Card */}
            <div className="relative p-4 bg-white/10 rounded-xl border border-white/10 overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>

                <div className="relative flex items-center gap-3">
                    <div className="relative shrink-0 cursor-pointer" onClick={() => setActiveAccountTab("customize")}>
                        <div className="ring-2 ring-amber-500/40 rounded-full p-0.5">
                            <Avatar src={avatar} className="w-16 h-16" />
                        </div>
                        <LevelBadge level={level} className="-bottom-0.5 -right-0.5 w-6 h-6 text-[10px]" />
                    </div>

                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 cursor-pointer" onClick={() => setActiveAccountTab("customize")}>
                            {flag && <img src={getPublicAssetUrl(`assets/flags/${flag}.svg`)} alt="" className="w-5 h-3.5 shrink-0 object-contain" draggable={false} />}
                            <p className="text-white font-changa-one text-lg tracking-wide truncate">{username}</p>
                            <div className="shrink-0 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(180deg, #34D058 0%, #28A745 100%)", boxShadow: "0 1px 3px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.2)" }}>
                                <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Stats pills */}
            <div className="grid grid-cols-3 gap-2">
                <StatPill icon="/assets/icons/trophy.webp" value={stats?.wins ?? 0} label={t("stats.wins")} />
                <StatPill icon="/assets/icons/broken-shield.webp" value={stats?.losses ?? 0} label={t("stats.losses")} />
                <StatPill icon="/assets/icons/target.webp" value={stats && stats.games > 0 ? `${Math.round((stats.wins / stats.games) * 100)}%` : "—"} label={t("stats.winrate")} />
                <StatPill icon="fan" value={stats?.games ?? 0} label={t("stats.games")} />
                <StatPill icon="/assets/icons/streak.webp" value={stats?.bestStreak ?? 0} label={t("stats.streak")} />
                <StatPill icon="/assets/icons/card.webp" value={stats?.lastCount ?? 0} label={t("stats.last")} />
            </div>
        </div>
    );
};

const StatPill = ({ icon, value, label }: { icon: string; value: string | number; label: string }) => (
    <div className="flex flex-col items-center gap-1 py-4 rounded-xl border border-white/10" style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)" }}>
        {icon === "fan" ? (
            <div className="relative w-10 h-7">
                <img src={getPublicAssetUrl("assets/icons/card.webp")} alt="" className="absolute w-7 h-7 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 -rotate-12" draggable={false} />
                <img src={getPublicAssetUrl("assets/icons/card.webp")} alt="" className="absolute w-7 h-7 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" draggable={false} />
                <img src={getPublicAssetUrl("assets/icons/card.webp")} alt="" className="absolute w-7 h-7 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rotate-12" draggable={false} />
            </div>
        ) : icon.startsWith("/") ? (
            <img src={getPublicAssetUrl(icon)} alt="" className="w-7 h-7" draggable={false} />
        ) : (
            <span className="text-xl">{icon}</span>
        )}
        <div className="flex items-center gap-0.5">
            <span className="text-white font-changa-one text-base">{value}</span>
        </div>
        <span className="text-white/50 text-sm font-changa-one tracking-wide text-center min-h-[2.5em] flex items-center">{label}</span>
    </div>
);

export default ProfileTab;
